import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Ruler, ArrowRight, Sparkles, Send, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPES = [
  { value: 'produce', label: 'Fruits & Vegetables', factor: 1.0 },
  { value: 'meat', label: 'Meats & Proteins', factor: 1.15 },
  { value: 'dairy', label: 'Dairy & Liquids', factor: 1.35 },
  { value: 'meals', label: 'Prepared Meals', factor: 1.1 },
  { value: 'supplements', label: 'Supplements & Powders', factor: 1.2 },
  { value: 'pharma', label: 'Pharma / Lab Samples', factor: 1.3 }
];

const MODELS = {
  medium: { name: 'D-Freeze Medium', segment: 'entrepreneur', blurb: 'Ideal for pilot runs and small, frequent batches.' },
  large: { name: 'D-Freeze Large', segment: 'entrepreneur', blurb: 'Great for steady production and growing product lines.' },
  xl: { name: 'D-Freeze XL', segment: 'commercial-scientific', blurb: 'Built for high-volume commercial and scientific throughput.' }
};

const CapacityCalculator = () => {
  const [productType, setProductType] = useState('produce');
  const [batchLbs, setBatchLbs] = useState('');
  const [perWeek, setPerWeek] = useState('1');
  const [result, setResult] = useState(null);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const emailRecommendation = async (e) => {
    e.preventDefault();
    if (!leadEmail) { toast.error('Please enter your email.'); return; }
    setSending(true);
    try {
      const type = PRODUCT_TYPES.find(t => t.value === productType)?.label || productType;
      const message = `Capacity calculator recommendation: ${result.name}.\n` +
        `Product type: ${type}\nBatch weight: ${result.lbs} lbs\nBatches per week: ${perWeek}\n` +
        `Effective load: ~${result.effective} lbs.`;
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: leadName || 'Calculator user',
        email: leadEmail,
        message,
        form_source: 'capacity-calculator'
      });
      setSent(true);
      toast.success("Sent! We've emailed your recommendation and our team will follow up.");
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const calculate = (e) => {
    e.preventDefault();
    const lbs = parseFloat(batchLbs) || 0;
    const type = PRODUCT_TYPES.find(t => t.value === productType) || PRODUCT_TYPES[0];
    const effective = lbs * type.factor;

    let key = 'medium';
    if (effective > 15) key = 'xl';
    else if (effective > 8) key = 'large';

    // Heavy weekly cadence pushes toward a larger unit
    const weekly = parseInt(perWeek, 10) || 1;
    if (weekly >= 5 && key === 'medium') key = 'large';
    if (weekly >= 7 && key === 'large') key = 'xl';

    setResult({ ...MODELS[key], effective: Math.round(effective), weekly, lbs });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="capacity-calculator">
      <SEO title="Freeze Dryer Size Calculator" description="Find the right D-Freeze freeze dryer for your batch volume and product type with our capacity calculator." keywords="freeze dryer size calculator, freeze dryer capacity, D-Freeze" />
      <Navbar />

      <section className="pt-44 pb-16 bg-gradient-to-br from-[#003456] to-[#002B47] section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]"><Ruler className="h-4 w-4" /> Capacity Planner</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3">Find your D-Freeze size</h1>
          <p className="text-lg text-white/85 mt-4 max-w-2xl mx-auto">Tell us what you're drying and how much, we'll recommend the right model for your operation.</p>
        </div>
      </section>

      <section className="py-16 section-padding">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <form onSubmit={calculate} className="bg-white rounded-3xl p-8 shadow-sm" data-testid="calculator-form">
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product type</label>
              <select value={productType} onChange={(e) => setProductType(e.target.value)} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="calc-product-type">
                {PRODUCT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fresh weight per batch (lbs)</label>
              <input type="number" min="0" step="0.5" value={batchLbs} onChange={(e) => setBatchLbs(e.target.value)} placeholder="e.g. 12" className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="calc-batch-lbs" required />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Batches per week</label>
              <select value={perWeek} onChange={(e) => setPerWeek(e.target.value)} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="calc-per-week">
                <option value="1">1 to 2</option>
                <option value="3">3 to 4</option>
                <option value="5">5 to 6</option>
                <option value="7">7+</option>
              </select>
            </div>
            <button type="submit" className="inline-flex items-center gap-2 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors" data-testid="calc-submit">
              Recommend a model <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="bg-white rounded-3xl p-8 shadow-sm flex flex-col justify-center" data-testid="calculator-result">
            {result ? (
              <div>
                <span className="inline-flex items-center gap-2 text-[#CE8946] font-semibold text-sm uppercase tracking-wider"><Sparkles className="h-4 w-4" /> Recommended</span>
                <h2 className="text-3xl font-bold text-[#0a0203] mt-2">{result.name}</h2>
                <p className="text-gray-600 mt-2">{result.blurb}</p>
                <p className="text-sm text-gray-500 mt-4">Based on ~{result.lbs} lbs/batch (effective load ~{result.effective} lbs) at your weekly cadence.</p>
                <Link to={`/products?segment=${result.segment}`} className="inline-flex items-center gap-2 mt-6 bg-[#CE8946] text-[#0a0203] font-semibold px-6 py-3 rounded-md hover:bg-[#00a9b1] transition-colors" data-testid="calc-view-model">
                  View {result.name} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="block text-[#003456] font-medium hover:underline mt-4 text-sm">Need help deciding? Talk to our team →</Link>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  {sent ? (
                    <p className="text-sm text-emerald-600 font-medium">✓ Recommendation sent, check your inbox shortly.</p>
                  ) : (
                    <form onSubmit={emailRecommendation} data-testid="calc-lead-form">
                      <p className="text-sm font-medium text-gray-700 mb-2">Email me this recommendation</p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input type="text" value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Name (optional)" className="flex-1 p-2.5 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none text-sm" data-testid="calc-lead-name" />
                        <input type="email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="you@email.com" className="flex-1 p-2.5 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none text-sm" data-testid="calc-lead-email" required />
                      </div>
                      <button type="submit" disabled={sending} className="inline-flex items-center gap-2 mt-3 bg-[#003456] text-white font-semibold px-5 py-2.5 rounded-md hover:bg-[#002B47] transition-colors disabled:opacity-60 text-sm" data-testid="calc-lead-submit">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {sending ? 'Sending...' : 'Email me this'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <Ruler className="h-12 w-12 mx-auto mb-3 text-[#003456]/30" />
                <p>Fill in your details and we'll recommend the right D-Freeze model.</p>
              </div>
            )}
          </div>
        </div>
        <p className="max-w-4xl mx-auto text-xs text-gray-400 mt-4 section-padding">This tool provides general guidance. For exact capacities and specifications, see each product page or contact our team.</p>
      </section>

      <Footer />
    </div>
  );
};

export default CapacityCalculator;
